import { getUserByID } from '@adornis/buchungsheld-db/api/users.js';
import '@adornis/entity-picker/client/x-entity-picker.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import * as m from '../../../../localization/out/messages.js';
import {
  adminAddUserToCompany,
  adminRemoveUserFromCompany,
  adminToggleAdminRole,
  getCompaniesForUser,
} from '../../../api/admin-users.js';
import { subscribeJobApplicationsByUser } from '../../../api/job-application.js';
import { Company } from '../../../db/company.js';
import { MatchingWeekRole } from '../../../db/enums/roles.js';
import { formatAccountDate } from '../../../shared/account-status.js';
import { translateEnum } from '../../translate-enum.js';
import { adminRouter } from './admin-routes.js';

export const AdminUserDetail = stateful((props: { userID: string }) => {
  // Bumped after every role / company change so the user + company queries refresh.
  const [refreshTick, setRefreshTick] = useState(0);
  const bump = () => setRefreshTick(refreshTick + 1);

  const userQuery = useQuery(
    () =>
      getUserByID(props.userID)({
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        createdAt: 1,
        lastActiveAt: 1,
        roles: { _id: 1, _class: 1, name: 1, contexts: 1, permissions: 1 },
      }),
    [props.userID, refreshTick],
  );

  const companiesQuery = useQuery(
    () => getCompaniesForUser(props.userID)({ _id: 1, name: 1 }),
    [props.userID, refreshTick],
  );

  const applicationsQuery = useQuery(
    // Explicit selection — we want the resolved `jobPosition` (with its
    // company) and `matchingScore`, but NOT the depth-1 default which would
    // also pull `user`/`userData` for each row.
    () =>
      subscribeJobApplicationsByUser(props.userID)({
        _id: 1,
        jobPositionId: 1,
        status: 1,
        createdAt: 1,
        interviewStart: 1,
        matchingScore: { total: 1 },
        jobPosition: {
          _id: 1,
          title: 1,
          company: { _id: 1, name: 1 },
        },
      }),
    [props.userID],
  );

  // ─── Admin role toggle ───
  const [togglingAdmin, setTogglingAdmin] = useState(false);
  const [roleMsg, setRoleMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const isAdmin = (userQuery.value?.roles ?? []).some(r => r.name === MatchingWeekRole.ADMIN);
  const handleToggleAdmin = async (next: boolean) => {
    setTogglingAdmin(true);
    setRoleMsg(null);
    try {
      await adminToggleAdminRole(props.userID, next)();
      bump();
    } catch (e: any) {
      setRoleMsg({ ok: false, text: e?.message ?? m.admin_user_detail_role_update_failed() });
    } finally {
      setTogglingAdmin(false);
    }
  };

  // ─── Add to company (entity picker) ───
  const [pickedCompany, setPickedCompany] = useState<{ _id: string; name?: string } | null>(null);
  const [addingCompany, setAddingCompany] = useState(false);
  const [companyMsg, setCompanyMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const handleAddCompany = async () => {
    if (!pickedCompany?._id) return;
    setAddingCompany(true);
    setCompanyMsg(null);
    try {
      await adminAddUserToCompany(props.userID, pickedCompany._id)();
      setPickedCompany(null);
      bump();
    } catch (e: any) {
      setCompanyMsg({ ok: false, text: e?.message ?? m.admin_user_detail_add_company_failed() });
    } finally {
      setAddingCompany(false);
    }
  };

  const handleRemoveCompany = async (companyID: string) => {
    setCompanyMsg(null);
    try {
      await adminRemoveUserFromCompany(props.userID, companyID)();
      bump();
    } catch (e: any) {
      setCompanyMsg({ ok: false, text: e?.message ?? m.admin_user_detail_remove_company_failed() });
    }
  };

  if (userQuery.loading) {
    return html`<div class="p-8 text-center"><span class="loading loading-lg"></span></div>`;
  }
  if (!userQuery.value) {
    return html`<div class="p-8">${m.admin_user_detail_user_not_found()}</div>`;
  }

  const u = userQuery.value;

  return html`
    <div class="p-8 space-y-6">
      <div class="flex items-center justify-between">
        <button @click=${() => adminRouter.goTo('users')} class="btn btn-ghost">
          <span class="icon-[material-symbols--arrow-back]"></span>
          ${m.admin_user_create_back_to_users()}
        </button>
        <h1 class="text-3xl font-bold">${u.firstName} ${u.lastName}</h1>
      </div>

      <!-- Profile (read-only) -->
      <div class="card bg-base-100 shadow">
        <div class="card-body gap-2 max-w-2xl">
          <h2 class="card-title text-2xl mb-2">${m.admin_user_detail_profile_title()}</h2>
          <div><span class="font-semibold">${m.admin_user_detail_email_label()}</span> ${u.email ?? '-'}</div>
          <div>
            <span class="font-semibold">${m.admin_user_detail_name_label()}</span> ${u.firstName ?? ''}
            ${u.lastName ?? ''}
          </div>
          <div>
            <span class="font-semibold">${m.admin_user_detail_created_at_label()}</span>
            ${formatAccountDate(u.has('createdAt') ? u.createdAt : null)}
          </div>
          <div>
            <span class="font-semibold">${m.admin_user_detail_last_active_at_label()}</span>
            ${
              u.has('lastActiveAt') && u.lastActiveAt
                ? formatAccountDate(u.lastActiveAt)
                : m.admin_user_detail_not_used_since_tracking()
            }
          </div>
        </div>
      </div>

      <!-- Roles + companies -->
      <div class="card bg-base-100 shadow">
        <div class="card-body gap-4">
          <h2 class="card-title text-2xl">${m.admin_user_detail_roles_title()}</h2>
          ${
            roleMsg
              ? html`<div class="alert ${roleMsg.ok ? 'alert-success' : 'alert-error'}">${roleMsg.text}</div>`
              : nothing
          }
          <label class="label cursor-pointer justify-start gap-3">
            <input
              type="checkbox"
              class="toggle toggle-primary"
              ?checked=${isAdmin}
              ?disabled=${togglingAdmin}
              @change=${(e: Event) => handleToggleAdmin((e.target as HTMLInputElement).checked)}
            />
            <span class="label-text">${m.admin_user_detail_matching_week_admin_label()}</span>
          </label>

          <div class="divider my-0"></div>

          <h3 class="font-semibold">${m.admin_user_detail_companies_title()}</h3>
          ${
            companyMsg
              ? html`<div class="alert ${companyMsg.ok ? 'alert-success' : 'alert-error'}">${companyMsg.text}</div>`
              : nothing
          }
          ${
            companiesQuery.loading
              ? html`<span class="loading loading-spinner"></span>`
              : (companiesQuery.value?.length ?? 0) === 0
                ? html`<div class="opacity-70">${m.admin_user_detail_no_companies()}</div>`
                : html`<ul class="list-disc pl-6 space-y-1">
                    ${repeat(
                      companiesQuery.value ?? [],
                      c => c._id ?? '',
                      c => html`
                        <li class="flex items-center gap-2">
                          <a
                            class="link link-primary"
                            href=${'/admin/company/' + c._id}
                            @click=${(e: Event) => {
                              e.preventDefault();
                              goTo('/admin/company/' + c._id);
                            }}
                            >${c.name ?? c._id}</a
                          >
                          <button
                            class="btn btn-ghost btn-xs"
                            title=${m.admin_user_detail_remove_from_company_title()}
                            @click=${() => handleRemoveCompany(c._id!)}
                          >
                            <span class="icon-[material-symbols--close] text-error"></span>
                          </button>
                        </li>
                      `,
                    )}
                  </ul>`
          }

          <div class="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
            <div class="flex-1 min-w-0">
              ${EntityPickerInput({
                class: Company,
                filter: new AdornisFilter({}),
                selectionSet: { _id: 1, name: 1 },
                value: pickedCompany?._id ?? null,
                placeholder: m.admin_user_detail_add_to_company_placeholder(),
                renderString: c => c?.name ?? c?._id ?? '',
                onEntityPicked: c => setPickedCompany(c ? { _id: c._id!, name: c.name } : null),
              } as any)}
            </div>
            <button
              class="btn btn-primary"
              ?disabled=${!pickedCompany?._id || addingCompany}
              @click=${handleAddCompany}
            >
              ${addingCompany ? html`<span class="loading loading-spinner"></span>` : nothing}
              ${m.admin_create_company_user_add_to_company()}
            </button>
          </div>
        </div>
      </div>

      <!-- Applications -->
      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <h2 class="card-title text-2xl mb-2">${m.applications()}</h2>
          ${
            applicationsQuery.loading
              ? html`<span class="loading loading-spinner"></span>`
              : (applicationsQuery.value?.length ?? 0) === 0
                ? html`<div class="opacity-70">${m.admin_user_detail_no_applications()}</div>`
                : html`
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>${m.admin_user_detail_column_company()}</th>
                          <th>${m.admin_user_detail_column_position()}</th>
                          <th>${m.admin_user_detail_column_status()}</th>
                          <th>${m.admin_user_detail_column_submitted()}</th>
                          <th>${m.admin_user_detail_column_interview()}</th>
                          <th>${m.admin_user_detail_column_score()}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        ${repeat(
                          applicationsQuery.value ?? [],
                          a => a._id ?? '',
                          a => {
                            const href = a._id
                              ? '/admin/application/' +
                                a._id +
                                '?returnTo=' +
                                encodeURIComponent('/admin/user/' + props.userID)
                              : null;
                            return html`
                              <tr>
                                <td>${a.jobPosition?.company?.name ?? '-'}</td>
                                <td>${a.jobPosition?.title ?? '-'}</td>
                                <td>${translateEnum('application_status', a.status ?? '-')}</td>
                                <td>${a.createdAt ? format(new Date(a.createdAt), 'dd.MM.yyyy, HH:mm') : '-'}</td>
                                <td>
                                  ${a.interviewStart ? format(new Date(a.interviewStart), 'dd.MM.yyyy, HH:mm') : '-'}
                                </td>
                                <td>
                                  ${
                                    a.matchingScore?.total !== undefined && a.matchingScore?.total !== null
                                      ? html`${a.matchingScore.total}%`
                                      : '-'
                                  }
                                </td>
                                <td>
                                  ${
                                    href
                                      ? html`<button class="btn btn-sm btn-ghost" @click=${() => goTo(href)}>
                                          ${m.admin_user_detail_open_application_button()}
                                        </button>`
                                      : nothing
                                  }
                                </td>
                              </tr>
                            `;
                          },
                        )}
                      </tbody>
                    </table>
                  `
          }
        </div>
      </div>
    </div>
  `;
});
